import {
  BAR_POINT,
  Dice,
  InProgressGameState,
  Model,
  OFF_POINT,
  opponent,
  opponentPoint,
  Player,
  playMoves,
  User,
} from '@kfiroo/backgammon-core'
import { DocumentReference } from 'firebase/firestore'

import { requestToken, TokenResult } from '../../firebase/messaging'
import { AnimatedMove, projectLiveMoves } from '../services/GameView'
import { PlayController } from '../services/PlayController'
import { BaseGameApi, BoardInfo, PlayerType } from './BaseGameApi'

export class InProgressGameApi extends BaseGameApi<InProgressGameState> {
  constructor(ref: DocumentReference, model: Model, user: User) {
    super(ref, model, user)
  }

  protected get playerBoard(): InProgressGameState['board'][Player] {
    return this.gameState.board[this.playerIndex as Player]
  }

  protected get opponentBoard(): InProgressGameState['board'][Player] {
    return this.gameState.board[opponent(this.playerIndex as Player)]
  }

  protected playerPoint(point: number): number {
    if (point < OFF_POINT || point > BAR_POINT) {
      throw new Error('Point out of range')
    }
    return this.playerBoard[point]
  }

  protected opponentPoint(point: number): number {
    if (point < OFF_POINT || point > BAR_POINT) {
      throw new Error('Point out of range')
    }
    return this.opponentBoard[opponentPoint(point)]
  }

  async setupNotifications(): Promise<TokenResult> {
    // Persist the committed-board model (not the tentative one) — see modelToSave.
    const model = this.modelToSave
    const user = model.players[this.playerIndex]
    if (!user) {
      return {
        success: false,
        error: 'Not a player',
      }
    }

    const result = await requestToken()

    if (result.success) {
      if (user.messagingToken === result.token) {
        return {
          success: true,
          token: user.messagingToken,
        }
      }

      user.messagingToken = result.token
      void this.saveGameModel(model).catch((err) =>
        console.error('setupNotifications save', err),
      )
    }
    return result
  }

  getBoard(): BoardInfo {
    return this.buildBoardInfoFromState(this.effectiveState())
  }

  getLiveMoves(): AnimatedMove[] {
    return projectLiveMoves(this.currentModel, this.playerIndex)
  }

  /**
   * The in-progress state to render: the committed board with any liveTurn moves
   * applied (the tentative position). For the active player, currentModel is the
   * display model with no liveTurn, so this returns it unchanged (no double-apply).
   */
  protected effectiveState(): InProgressGameState {
    const gs = this.gameState
    const live = gs.liveTurn
    // Derive only when a live turn has moves. The dice guard is defensive against
    // a malformed remote doc (liveTurn present without a roll); our own writes
    // never produce that. steps are unused here — this is a display-only derivation.
    if (gs.dice !== null && live != null && live.moves.length > 0) {
      const { model } = playMoves(this.currentModel, live.moves, [])
      // playMoves only applies in-turn moves (it never ends the turn or game),
      // so the resulting gameState is always still in-progress.
      return model.gameState as InProgressGameState
    }
    return gs
  }

  getTurn(): PlayerType {
    return this.gameState.turn === this.playerIndex
      ? PlayerType.PLAYER
      : PlayerType.OPPONENT
  }

  /** The authoritative turn pointer (0 or 1). Unlike getTurn(), this changes
   *  every turn for every viewer — including spectators — so the animated board
   *  can detect a turn boundary and reset rather than reverse. */
  getTurnIndex(): number {
    return this.gameState.turn
  }

  getDice(): Dice | null {
    return this.gameState.dice
  }

  getRules(): Model['rules'] {
    return this.model.rules
  }

  getUserId(): string {
    return this.user.uid
  }

  isParticipant(): boolean {
    return this.playerIndex !== -1
  }

  getOpponentUserId(): string | null {
    if (this.playerIndex === -1) {
      return null
    }
    return this.currentModel.players[1 - this.playerIndex]?.uid ?? null
  }

  get playable(): boolean {
    return false
  }
}

export class PlayingGameApi extends InProgressGameApi {
  private controller: PlayController

  constructor(ref: DocumentReference, model: Model, user: User) {
    super(ref, model, user)
    this.controller = new PlayController(this.model)
  }

  override get playable(): boolean {
    return true
  }

  protected override get currentModel(): Model {
    return this.controller.model
  }

  protected override get modelToSave(): Model {
    return this.controller.persistModel
  }

  async rollDice(): Promise<void> {
    const before = this.controller.snapshotState()
    this.controller.rollDice() // optimistic: dice visible immediately via currentModel
    try {
      await this.saveGameModel(this.controller.persistModel)
    } catch (err) {
      this.controller.restoreState(before) // roll back the optimistic dice
      throw err
    }
  }

  getValidMoves() {
    return this.controller.getValidMoves()
  }

  async move(from: number, to: number): Promise<void> {
    const before = this.controller.snapshotState()
    this.controller.move(from, to) // optimistic: tentative board visible immediately
    try {
      await this.saveGameModel(this.controller.persistModel)
    } catch (err) {
      this.controller.restoreState(before) // roll back the optimistic move
      throw err
    }
  }

  isOpenOpponent(point: number): boolean {
    return this.opponentPoint(point) === 1
  }

  canEndTurn(): boolean {
    return this.controller.canEndTurn()
  }

  async endTurn(): Promise<void> {
    const before = this.controller.snapshotState()
    let model: Model
    try {
      model = this.controller.endTurn(this.initialModel)
    } catch (err) {
      // incomplete turn: core playTurn threw — validation no-op, unchanged (no persist)
      console.error('endTurn', err)
      return
    }
    try {
      await this.saveGameModel(model)
    } catch (err) {
      this.controller.restoreState(before) // roll back the optimistic turn application
      throw err
    }
  }

  canUndo(): boolean {
    return this.controller.canUndo()
  }

  async undo(): Promise<void> {
    const before = this.controller.snapshotState()
    this.controller.undo() // optimistic
    try {
      await this.saveGameModel(this.controller.persistModel)
    } catch (err) {
      this.controller.restoreState(before) // roll back the optimistic undo
      throw err
    }
  }
}

export class ReadOnlyGameApi extends InProgressGameApi {
  constructor(ref: DocumentReference, model: Model, user: User) {
    super(ref, model, user)
  }
}

function isUserPlaying(model: Model, user: User): boolean {
  if (model.gameState.state !== 'in-progress') {
    throw new Error('Game is not in progress')
  }
  const playerIndex = model.players.findIndex((u) => u?.email === user.email)
  return playerIndex === model.gameState.turn
}

export const getInProgressGameApi = (
  ref: DocumentReference,
  model: Model,
  user: User,
): InProgressGameApi => {
  const searchParams = new URLSearchParams(document.location.search)
  const playerIndex = searchParams.get('p')
  if (playerIndex === '0' || playerIndex === '1') {
    return new PlayingGameApi(
      ref,
      model,
      model.players[playerIndex === '0' ? 0 : 1]!,
    )
  }

  if (isUserPlaying(model, user)) {
    return new PlayingGameApi(ref, model, user)
  }
  return new ReadOnlyGameApi(ref, model, user)
}
